import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/king-of-the-court-generator')({
    component: RouteComponent,
})

type Court = {label: string; isKing: boolean; teamA: string; teamB: string};
type KotcRound = {title: string; courts: Court[]};

function buildKingOfCourt(activePlayers: string[], activeCourts: number, rounds: number): KotcRound[] {
    const result: KotcRound[] = [];
    let rotation = [...activePlayers];

    for (let round = 1; round <= rounds; round++) {
        const courts: Court[] = [];
        for (let court = 1; court <= activeCourts; court++) {
            const start = (court - 1) * 4;
            courts.push({
                label: court === 1 ? "👑 King Court" : `Court ${court}`,
                isKing: court === 1,
                teamA: `${rotation[start]} & ${rotation[start + 1]}`,
                teamB: `${rotation[start + 2]} & ${rotation[start + 3]}`,
            });
        }
        result.push({title: `Round ${round}`, courts});

        // Demo rotation: move the last group of four to the front for the next round.
        const groups: string[][] = [];
        for (let i = 0; i < rotation.length; i += 4) groups.push(rotation.slice(i, i + 4));
        const last = groups.pop();
        if (last) groups.unshift(last);
        rotation = groups.flat();
    }

    return result;
}

const STEPS = [
    {title: "Start on Ranked Courts", description: "Players or teams are assigned across courts from lowest to highest."},
    {title: "Play Short Rounds", description: "Matches are usually timed or played to a set number of points."},
    {title: "Winners Move Up", description: "The winning side moves toward the King Court, while losers move down."},
];

const VS_ROWS = [
    {feature: "Main idea", k: "Winners move up courts", a: "Players rotate partners and opponents"},
    {feature: "Best for", k: "Fast competitive club nights", a: "Social mixing events"},
    {feature: "Match style", k: "Short timed rounds", a: "Fixed rotation rounds"},
    {feature: "Competitive feel", k: "High", a: "Medium"},
    {feature: "Common sports", k: "Padel, pickleball, tennis", a: "Padel, pickleball, tennis"},
];

const BEST_FOR = ["Padel club nights", "Pickleball social events", "Tennis doubles sessions", "Fast-paced court rotations", "Mixed ability groups", "Events with limited time per round"];
const LESS_IDEAL = ["Formal knockout tournaments", "Full league standings", "Events where everyone must play everyone", "Singles-only formats", "Competitions needing detailed rankings"];

const FAQS = [
    {
        q: 'What is King of the Court?',
        a: <>King of the Court is a fast-paced tournament format where winners move toward the top court and losers move down or rotate out.</>,
    },
    {
        q: 'How many players do you need?',
        a: <>For doubles, you need at least four players per active court. Extra players can rotate in between rounds.</>,
    },
    {
        q: 'Is King of the Court good for padel?',
        a: <>Yes. It works very well for padel because it creates short, competitive matches and keeps players moving between courts.</>,
    },
    {
        q: 'How do players move between courts?',
        a: <>Typically winners move up one court and losers move down one court. Winners on the top court remain on the King Court.</>,
    },
    {
        q: 'Can ScorePal manage King of the Court events?',
        a: <>Yes. ScorePal manages fixtures, court rotations, scores, rankings and live event communication.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [courts, setCourts] = useState("2");
    const [rounds, setRounds] = useState("4");
    const [schedule, setSchedule] = useState<KotcRound[] | null>(null);
    const [resting, setResting] = useState<string[]>([]);
    const [summary, setSummary] = useState<{players: number; courts: number; rounds: number; resting: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Ranking the courts", "Seeding the first round", "Mapping promotions and relegations"]);

    const generate = () => {
        const players = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (players.length < 4) {
            setError("Please enter at least four players.");
            setSchedule(null);
            setSummary(null);
            return;
        }
        const courtCount = Math.min(8, Math.max(1, parseInt(courts, 10) || 2));
        const roundCount = Math.min(12, Math.max(1, parseInt(rounds, 10) || 4));
        const activeCourts = Math.min(courtCount, Math.floor(players.length / 4));
        const activePlayers = players.slice(0, activeCourts * 4);
        const restingPlayers = players.slice(activeCourts * 4);

        setError(null);
        setSchedule(null);
        setSummary(null);
        gen.run(() => {
            setSchedule(buildKingOfCourt(activePlayers, activeCourts, roundCount));
            setResting(restingPlayers);
            setSummary({players: players.length, courts: activeCourts, rounds: roundCount, resting: restingPlayers.length});
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>King of the Court Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a King of the Court tournament schedule instantly. Generate court rotations for padel, tennis, pickleball and club events with ScorePal's free King of the Court generator." />
            <link rel="canonical" href={`${config.publicUrl}/king-of-the-court-generator/`} />
            <meta property="og:title" content="King of the Court Generator | ScorePal" />
            <meta property="og:description" content="Generate King of the Court rotations for padel, tennis, pickleball and social club events." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/king-of-the-court-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/king-of-the-court-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="King of the Court Generator | ScorePal" />
            <meta name="twitter:description" content="Create King of the Court schedules and rotations instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "King of the Court Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate King of the Court rotations and schedules for padel, tennis, pickleball and club events.",
                "url": "https://scorepal.co/king-of-the-court-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is King of the Court?", "acceptedAnswer": {"@type": "Answer", "text": "King of the Court is a fast-paced tournament format where winners move up toward the top court and losers move down or rotate out."}},
                    {"@type": "Question", "name": "What sports use King of the Court?", "acceptedAnswer": {"@type": "Answer", "text": "King of the Court is popular in padel, tennis, pickleball, beach volleyball and other court-based sports."}},
                    {"@type": "Question", "name": "How does King of the Court work?", "acceptedAnswer": {"@type": "Answer", "text": "Players or teams play short timed rounds. Winners move closer to the king court, while losing teams move down or rotate to a lower court."}},
                    {"@type": "Question", "name": "How many players do you need?", "acceptedAnswer": {"@type": "Answer", "text": "For doubles, you need at least four players per active court. Extra players can rotate in between rounds."}},
                    {"@type": "Question", "name": "Can ScorePal manage King of the Court events?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, court rotations, scores, rankings and live event communication."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "King of the Court Generator", item: "https://scorepal.co/king-of-the-court-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Court Rotation Tool
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    King of the Court Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create King of the Court rotations for padel, tennis, pickleball and fast-paced club events.
                                    Generate courts, matchups and movement rules so winners move up toward the king court.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Rotation</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players, courts and rounds to build the rotation.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4\nPlayer 5\nPlayer 6\nPlayer 7\nPlayer 8"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <label htmlFor="courts" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Courts</label>
                                        <input id="courts" type="number" min={1} max={8} value={courts} onChange={(e) => setCourts(e.target.value)}
                                               className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40" />
                                    </div>
                                    <div>
                                        <label htmlFor="rounds" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Rounds</label>
                                        <input id="rounds" type="number" min={1} max={12} value={rounds} onChange={(e) => setRounds(e.target.value)}
                                               className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40" />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate King of the Court"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.players}</strong> players · <strong>{summary.courts}</strong> active court{summary.courts > 1 ? "s" : ""} ·{" "}
                                        <strong>{summary.rounds}</strong> rounds
                                        {summary.resting ? <> · <strong>{summary.resting}</strong> resting/rotating</> : null}
                                    </p>
                                )}

                                {schedule && <GeneratorTopCta note="Run it live on ScorePal with scores, a leaderboard and a shareable page." />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {schedule && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                            Your King of the Court Schedule
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 mb-8">Court 1 is the King Court. Winners move up, losers move down or rotate.</p>

                        <div className="space-y-6">
                            {schedule.map((round) => (
                                <div key={round.title} className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-5">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold mb-4">{round.title}</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {round.courts.map((court, i) => (
                                            <div key={i} className={`rounded-2xl border p-4 ${court.isKing ? "border-[#fdb022]/70 bg-[#fffbeb]" : "border-[#e5e7eb] bg-white"}`}>
                                                <div className={`font-inter font-bold mb-2 ${court.isKing ? "text-[#b54708]" : "text-[#1a1a2e]"}`}>{court.label}</div>
                                                <div className="font-inter text-sm font-semibold text-[#1a1a2e]">{court.teamA}</div>
                                                <div className="font-inter text-xs text-[#1a1a2e]/40 my-0.5">vs</div>
                                                <div className="font-inter text-sm font-semibold text-[#1a1a2e]">{court.teamB}</div>
                                                <div className="font-inter text-xs text-[#1a1a2e]/55 mt-3">Winners move up · Losers move down</div>
                                            </div>
                                        ))}
                                    </div>
                                    {resting.length > 0 && (
                                        <p className="mt-3 font-inter text-xs text-[#1a1a2e]/60">
                                            <strong>Rotating/resting:</strong> {resting.join(", ")}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex justify-center">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                <span className="font-inter text-base font-medium">Run this event with ScorePal</span>
                                <div className="bg-white rounded-full p-1">
                                    <ArrowUpRightIcon className="text-[#082F8A]" size={28} />
                                </div>
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* What is KOTC / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is King of the Court?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        King of the Court is a fast-paced tournament format where teams compete on ranked courts. Winners move
                        up toward the King Court, while losing teams move down or rotate out. It is popular for padel, tennis,
                        pickleball and other court-based sports.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* KOTC vs Americano */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        King of the Court vs Americano
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Both formats work well for social doubles events, but they create different player experiences. Compare with the <Link to={"/americano-generator/" as any} className="text-[#082F8A] underline font-semibold">Americano generator</Link>.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">King of the Court</div>
                                <div className="p-5 text-[#1a1a2e]">Americano</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.k}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.a}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use King of the Court?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your King of the Court Event With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate rotations, manage courts, collect scores and publish live results from one tournament management platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["King of the Court", "Padel", "Tennis", "Pickleball", "Court Rotations", "Live Scores"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"King of the Court FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
